/**
 * Mini synth programático — zero asset, baixo peso, sem autoplay issues.
 * A instância do AudioContext é lazy: só nasce no primeiro `play*()` para
 * respeitar a policy de user-gesture dos browsers.
 */
let ctx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  if (ctx) return ctx;
  const W = window as Window & {
    webkitAudioContext?: typeof AudioContext;
  };
  const Ctor = window.AudioContext ?? W.webkitAudioContext;
  if (!Ctor) return null;
  ctx = new Ctor();
  return ctx;
}

/**
 * "Pop" satisfatório — soa como uma gota caindo numa bowl. Perfeito para
 * confirmar um clique de escolha sem ser intrusivo.
 *
 * Design sonoro:
 *   - Osc 1: senóide 420 → 820 Hz (pitch bend ascendente em 80ms)
 *   - Osc 2: harmônico (triangle, dobro da fundamental) para dar brilho
 *   - Envelope: ataque rápido (15ms), decay suave (220ms), sem sustain
 */
export function playPop(): void {
  const c = getCtx();
  if (!c) return;
  try {
    if (c.state === "suspended") void c.resume();

    const now = c.currentTime;
    const master = c.createGain();
    master.connect(c.destination);
    master.gain.setValueAtTime(0.0001, now);
    master.gain.exponentialRampToValueAtTime(0.22, now + 0.015);
    master.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);

    const osc1 = c.createOscillator();
    osc1.type = "sine";
    osc1.frequency.setValueAtTime(420, now);
    osc1.frequency.exponentialRampToValueAtTime(820, now + 0.08);

    const osc2 = c.createOscillator();
    osc2.type = "triangle";
    osc2.frequency.setValueAtTime(840, now);
    osc2.frequency.exponentialRampToValueAtTime(1640, now + 0.08);

    const osc2Gain = c.createGain();
    osc2Gain.gain.value = 0.35;

    osc1.connect(master);
    osc2.connect(osc2Gain);
    osc2Gain.connect(master);

    osc1.start(now);
    osc2.start(now);
    osc1.stop(now + 0.25);
    osc2.stop(now + 0.25);
  } catch {
    /* silent fail — áudio é enhancement, não crítico */
  }
}
